import { SearchApi } from '../api';
import { DocumentParser, SuggestionsParser } from './parsers';
import { SearchDocumentType, } from './sdk.types';
import { getDemoSearchResponse, getDemoSampleResponse, } from './demoContent';
import { apiErrorParser } from './parsers/apiErrorParser';
import { getDemoSuggestionsContent } from './demoContent/suggestionsResults';
const PROPERTIES_FOR_STORES = [{ name: 'result-format', value: 'store-front' }];
const validDocumentTypes = Object.values(SearchDocumentType);
function getSearchApiDocumentType(documentType) {
    if (documentType === SearchDocumentType.All) {
        return;
    }
    return documentType;
}
const getQuery = (query) => query || '*';
export class ClientSearchSDK {
    constructor({ httpClient, timeout, siteBaseUrl, language, excludeDocumentTypes = [], useRawProductDocuments = false, }) {
        this.useRawProductDocuments = useRawProductDocuments;
        this.language = language;
        this.api = new SearchApi({
            httpClient,
            timeout,
        });
        this.excludeDocumentTypes = excludeDocumentTypes;
        this.useRawProductDocuments = useRawProductDocuments;
        this.documentParser = new DocumentParser(siteBaseUrl, language);
        this.suggestionsParser = new SuggestionsParser(siteBaseUrl, language);
    }
    async search(searchRequest, requestParams) {
        try {
            const properties = this.useRawProductDocuments &&
                searchRequest.documentType === SearchDocumentType.Products
                ? PROPERTIES_FOR_STORES
                : [];
            const request = {
                ...searchRequest,
                documentType: getSearchApiDocumentType(searchRequest.documentType),
                query: getQuery(searchRequest.query),
                language: this.language,
                paging: searchRequest.paging && {
                    skip: (searchRequest.paging.page - 1) * searchRequest.paging.pageSize,
                    limit: searchRequest.paging.pageSize,
                },
                properties,
            };
            const data = this.demoContentOptions
                ? getDemoSearchResponse(request, this.demoContentOptions)
                : (await this.api.search(request, requestParams)).data;
            const documents = data.documents.map((document) => this.documentParser.parse(document, this.useRawProductDocuments));
            const { facets } = data;
            const totalResults = data.nextPage ? data.nextPage.total : 0;
            return {
                documents,
                facets,
                totalResults,
            };
        }
        catch (ex) {
            return {
                errorDetails: apiErrorParser(ex?.response?.data),
                isError: true,
            };
        }
    }
    useDemoContent(options) {
        this.demoContentOptions = options;
    }
    parseFederatedResults(results, titleParser) {
        return results
            .filter(({ documentType }) => validDocumentTypes.includes(documentType) &&
            !this.excludeDocumentTypes.includes(documentType))
            .map((sample) => ({
            ...sample,
            title: titleParser(sample.documentType),
            documents: sample.documents.map((document) => this.documentParser.parse(document, this.useRawProductDocuments)),
        }));
    }
    async getFederatedSuggestions(suggestionsRequest, requestParams) {
        const properties = this.useRawProductDocuments ? PROPERTIES_FOR_STORES : [];
        const extendedSuggestionsRequest = {
            ...suggestionsRequest,
            query: getQuery(suggestionsRequest.query),
            limit: suggestionsRequest.limit || 10,
            language: this.language,
            properties,
        };
        const response = await this.api.getFederatedSuggestions(extendedSuggestionsRequest, requestParams);
        if (this.demoContentOptions) {
            const demoDocumentTypes = response.data.results.map(({ documentType }) => documentType);
            const demoContent = getDemoSuggestionsContent(demoDocumentTypes);
            const results = this.parseFederatedResults(demoContent, this.suggestionsParser.getDocumentTypeTitle);
            return { results };
        }
        const results = this.parseFederatedResults(response.data.results, this.suggestionsParser.getDocumentTypeTitle);
        return {
            results,
        };
    }
    async getTrendingItems(trendingRequest, requestParams) {
        const properties = this.useRawProductDocuments ? PROPERTIES_FOR_STORES : [];
        const response = await this.api.getTrendingItems({
            ...trendingRequest,
            language: this.language,
            properties,
        }, requestParams);
        const results = this.parseFederatedResults(response.data.results, this.suggestionsParser.getDocumentTypeTrendingTitle);
        return { results };
    }
    async getFederatedAutocomplete(autocompleteRequest, requestParams) {
        if (this.demoContentOptions) {
            return {
                results: [
                    {
                        documentType: SearchDocumentType.Products,
                        values: [
                            { query: 'Search suggestion 1' },
                            { query: 'Search suggestion 2' },
                            { query: 'Search suggestion 3' },
                            { query: 'Search suggestion 4' },
                        ],
                    },
                ],
            };
        }
        const response = await this.api.getFederatedAutocomplete({
            ...autocompleteRequest,
            query: getQuery(autocompleteRequest.query),
            limit: autocompleteRequest.limit || 1,
            language: this.language,
        }, requestParams);
        const results = response.data.results.filter(({ documentType }) => validDocumentTypes.includes(documentType) &&
            !this.excludeDocumentTypes.includes(documentType));
        return {
            results,
        };
    }
    async getSample(sampleRequest, requestParams) {
        try {
            const request = {
                ...sampleRequest,
                query: sampleRequest.query && getQuery(sampleRequest.query),
                language: this.language,
            };
            const shouldUseEmptyResponse = this.demoContentOptions &&
                !this.demoContentOptions.shouldHaveSearchResults;
            let data = shouldUseEmptyResponse
                ? { results: [] }
                : (await this.api.getSample(request, requestParams)).data;
            if (this.demoContentOptions) {
                const demoDocumentTypes = data.results.map(({ documentType }) => documentType);
                // NOTE: We make a real request in editor demo mode to get available document types
                // (for tab visibility), but patch actual response documents with demo content.
                data = getDemoSampleResponse(request, demoDocumentTypes);
            }
            const documentsMapper = (document) => this.documentParser.parse(document, false);
            const results = data.results
                .filter(({ documentType }) => validDocumentTypes.includes(documentType) &&
                !this.excludeDocumentTypes.includes(documentType))
                .map((sample) => ({
                ...sample,
                documents: sample.documents.map(documentsMapper),
            }));
            return {
                results,
            };
        }
        catch (ex) {
            return {
                errorDetails: apiErrorParser(ex?.response?.data),
                isError: true,
            };
        }
    }
}
//# sourceMappingURL=sdk.js.map